import {
    Button,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { QuietButton } from './PatientListButtons';

export type PickerMenuEntry = {
    key: string;
    label: string;
    /** A second line under the label. */
    detail?: string;
    /** The mark beside the label, such as a check on the current value. */
    icon?: ReactNode;
    selected?: boolean;
    onPick: () => void;
};

type PickerMenuProps = {
    /** What the button is read out as. */
    ariaLabel: string;
    /** Shown on hover. */
    title?: string;
    disabled?: boolean;
    sx?: SxProps<Theme>;
    /** What the button shows. */
    children: ReactNode;
    entries: readonly PickerMenuEntry[];
    /**
     * Drawn as an outlined field, so it reads as something to edit; otherwise
     * as quiet inline text.
     */
    field?: boolean;
};

/**
 * A button that opens a dense menu of entries, each picked with one click,
 * which closes the menu.
 */
export default function PickerMenu({
    ariaLabel,
    title,
    disabled = false,
    sx,
    children,
    entries,
    field = false,
}: PickerMenuProps) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const close = () => setAnchor(null);
    const trigger = {
        disabled,
        onClick: (event: MouseEvent<HTMLElement>) =>
            setAnchor(event.currentTarget),
        'aria-haspopup': 'menu' as const,
        'aria-expanded': anchor !== null,
        'aria-label': ariaLabel,
        title,
        sx,
        children,
    };

    return (
        <>
            {field ? (
                <Button variant="outlined" size="small" {...trigger} />
            ) : (
                <QuietButton {...trigger} />
            )}
            <Menu
                open={anchor !== null}
                anchorEl={anchor}
                onClose={close}
                MenuListProps={{ dense: true }}
            >
                {entries.map((entry) => (
                    <MenuItem
                        key={entry.key}
                        selected={entry.selected}
                        onClick={() => {
                            close();
                            entry.onPick();
                        }}
                    >
                        <ListItemIcon>{entry.icon}</ListItemIcon>
                        <ListItemText
                            primary={entry.label}
                            secondary={entry.detail}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
